
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import type { Application, HealthPlan } from '@/types/database.types';
import { CarrierInformation } from './insurance/CarrierInformation';

interface InsuranceDetailsProps {
  data: Partial<Application>;
  onSave: (data: Partial<Application>) => void;
}

export const InsuranceDetails = ({ data, onSave }: InsuranceDetailsProps) => {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  
  const { register, handleSubmit, watch, setValue, formState: { isDirty } } = useForm<Partial<Application>>({
    defaultValues: data
  });

  // Helper function to ensure health plans have all required fields
  const processHealthPlans = (plans: any[]): HealthPlan[] => {
    if (!plans || !Array.isArray(plans)) return [];
    
    return plans.map(plan => ({
      plan_name: plan.plan_name || '',
      plan_type: plan.plan_type || '',
      carrier: plan.carrier || '',
      annual_premium: plan.annual_premium || '',
      employee_contribution: plan.employee_contribution || '',
      employer_contribution: plan.employer_contribution || ''
    }));
  };

  // Helper function to process form data before saving
  const processFormData = (formData: any): Partial<Application> => {
    return {
      ...formData,
      desired_medical_carriers: Array.isArray(formData.desired_medical_carriers) 
        ? formData.desired_medical_carriers 
        : [],
      desired_ancillary_carriers: Array.isArray(formData.desired_ancillary_carriers) 
        ? formData.desired_ancillary_carriers 
        : [],
      health_plans: processHealthPlans(formData.health_plans || []),
      // Handle string carrier fields
      current_medical_carrier: formData.current_medical_carrier || '',
      current_ancillary_carrier: formData.current_ancillary_carrier || '',
      // Handle boolean fields
      is_self_funded: Boolean(formData.is_self_funded),
      has_high_cost_claims: Boolean(formData.has_high_cost_claims),
      medical_with_peo: Boolean(formData.medical_with_peo),
      workers_comp_with_peo: Boolean(formData.workers_comp_with_peo),
      ancillary_with_peo: Boolean(formData.ancillary_with_peo)
    };
  };

  // Auto-save when form data changes
  useEffect(() => {
    if (isDirty) {
      const subscription = watch((value) => {
        console.log('Insurance form data changed:', value);
        // Process the data before triggering save
        const processedData = processFormData(value);
        onSave(processedData);
      });
      return () => subscription.unsubscribe();
    }
  }, [watch, onSave, isDirty]);

  const onSubmit = handleSubmit(async (formData) => {
    try {
      setIsSaving(true);
      console.log('Saving insurance details form data:', formData);
      
      const processedData = processFormData(formData);
      console.log('Processed insurance data to save:', processedData);
      
      await onSave(processedData);
      
      toast({
        title: "Insurance details saved",
        description: "Insurance information has been updated successfully.",
        variant: "default"
      });
    } catch (error) {
      console.error('Error saving insurance details:', error);
      toast({
        title: "Error saving data",
        description: "There was a problem saving the insurance information.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <form onSubmit={onSubmit}>
      <Card className="border-primary/5 shadow-elevation-1 overflow-hidden">
        <CardContent className="pt-6 space-y-6">
          <div className="flex items-center justify-between gap-3 pb-2 border-b border-gray-100">
            <h2 className="text-2xl font-semibold">Benefits Details</h2>
          </div>
          
          <div className="bg-primary/5 p-4 rounded-lg border border-primary/10 flex items-start gap-3">
            <p className="text-muted-foreground text-sm">
              Current insurance information helps identify optimal coverage transitions and ensures continuity—while enabling PEO providers to present the most competitive alternatives.
            </p>
          </div>
          
          <div className="space-y-8">
            {/* Carrier Information - now contains all fields including the moved ones */}
            <div className="bg-white p-5 rounded-lg border border-gray-100 shadow-sm">
              <CarrierInformation
                register={register}
                watch={watch}
                setValue={setValue}
                data={data}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};
